import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Plus, Trash2, Save, ChevronDown, ChevronRight, Paperclip, X, Clock, MoreVertical, Download, Upload, FileSpreadsheet, History } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import type { Employee } from '../types';
import {
  getApiBaseUrlNoPath,
  securityCommandControlLogApi,
  type SecurityCommandControlLogEntry,
  type SecurityCommandControlLogIncident,
} from '../services/api';
import { exportSecurityCommandControlLogEntry } from '../utils/securityCommandControlLogExport';
import { SecurityCommandControlLogBulkDeleteModal } from '../components/SecurityCommandControlLogBulkDeleteModal';
import { ImportSecurityCommandControlLogIncidentsModal } from '../components/ImportSecurityCommandControlLogIncidentsModal';
import { appConfirm } from '../context/SystemDialogContext';
import { SecurityCommandControlLogImportHistoryModal } from '../components/SecurityCommandControlLogImportHistoryModal';

type ExportKind = 'pdf' | 'excel' | 'csv';

type IncidentFormState = {
  incidentNo: string;
  incidentTime: string;
  location: string;
  details: string;
  actionBy: string;
  outcome: string;
  photosCctvDescription: string;
};

/** Normalize stored incident time for HTML `type="time"` (HH:mm). */
function toHtmlTimeValue(raw: string): string {
  const v = raw?.trim();
  if (!v) return '';
  const iso = v.match(/T(\d{2}):(\d{2})/);
  if (iso) return `${iso[1]}:${iso[2]}`;
  const m = v.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m) {
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
      return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    }
  }
  return '';
}

interface EntryExpandedBodyProps {
  entry: SecurityCommandControlLogEntry;
  employee: Employee | null;
  saving: boolean;
  getIncidentForm: (entryId: number) => IncidentFormState;
  setIncidentFormField: (entryId: number, field: string, value: string) => void;
  attachmentUrl: (path: string) => string;
  handleDeleteIncident: (entryId: number, incidentId: number) => void;
  handleDeleteAttachment: (entryId: number, incidentId: number, attachmentId: number) => void;
  handleUpload: (entryId: number, incident: SecurityCommandControlLogIncident, files: FileList | null) => void;
  onOpenAddIncident: (entryId: number) => void;
}

const EntryExpandedBody: React.FC<EntryExpandedBodyProps> = ({
  entry,
  employee,
  saving,
  getIncidentForm,
  setIncidentFormField,
  attachmentUrl,
  handleDeleteIncident,
  handleDeleteAttachment,
  handleUpload,
  onOpenAddIncident,
}) => {
  const incidents = entry.incidents || [];

  return (
    <div className="space-y-4 text-sm md:text-[15px] md:leading-relaxed">
      <div className="text-neutral-700">
        <span className="font-medium">Logger: </span>
        {employee ? `${employee.firstName} ${employee.lastName}` : '—'}
        {entry.loggerEmployeeId !== employee?.id && (
          <span className="text-neutral-500"> (recorded as #{entry.loggerEmployeeId})</span>
        )}
      </div>

      <div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
          <h3 className="font-semibold text-neutral-800">Incidents</h3>
          <button
            type="button"
            onClick={() => onOpenAddIncident(entry.id)}
            disabled={saving}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 shrink-0 md:text-[15px]"
          >
            <Plus className="h-4 w-4" />
            New incident
          </button>
        </div>

        {/* Mobile: card list */}
        <div className="md:hidden space-y-3">
          {incidents.map((inc) => (
            <div key={inc.id} className="p-3 bg-white border border-neutral-200 rounded-md text-sm space-y-2">
              <div className="flex justify-between gap-2">
                <span className="font-medium">#{inc.incidentNo}</span>
                <button
                  type="button"
                  className="text-red-600 text-xs"
                  onClick={() => handleDeleteIncident(entry.id, inc.id)}
                >
                  Delete
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-neutral-700">
                <div>
                  <span className="text-neutral-500">Time: </span>
                  {inc.incidentTime || '—'}
                </div>
                <div>
                  <span className="text-neutral-500">Location: </span>
                  {inc.location || '—'}
                </div>
              </div>
              <div>
                <span className="text-neutral-500">Details: </span>
                {inc.details || '—'}
              </div>
              <div>
                <span className="text-neutral-500">Action by: </span>
                {inc.actionBy || '—'}
              </div>
              <div>
                <span className="text-neutral-500">Outcome: </span>
                {inc.outcome || '—'}
              </div>
              {inc.photosCctvDescription && (
                <div>
                  <span className="text-neutral-500">Photos / CCTV note: </span>
                  {inc.photosCctvDescription}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {(inc.attachments || []).map((a) => (
                  <span key={a.id} className="inline-flex items-center gap-1 text-xs bg-neutral-100 px-2 py-1 rounded">
                    <a
                      href={attachmentUrl(a.filePath)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {a.fileName}
                    </a>
                    <button
                      type="button"
                      className="text-red-500"
                      onClick={() => handleDeleteAttachment(entry.id, inc.id, a.id)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <label className="inline-flex items-center gap-1 text-xs text-blue-600 cursor-pointer">
                  <Paperclip className="h-3 w-3" />
                  Add files
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    disabled={saving}
                    onChange={(e) => {
                      handleUpload(entry.id, inc, e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block overflow-x-auto rounded-md border border-neutral-200 bg-white">
          <table className="min-w-full text-left">
            <thead className="bg-neutral-50 text-neutral-600 border-b border-neutral-200">
              <tr>
                <th className="px-3 py-2 font-medium whitespace-nowrap">Incident no.</th>
                <th className="px-3 py-2 font-medium whitespace-nowrap">Time</th>
                <th className="px-3 py-2 font-medium min-w-[7rem]">Location</th>
                <th className="px-3 py-2 font-medium min-w-[10rem] max-w-xs">Details</th>
                <th className="px-3 py-2 font-medium min-w-[6rem]">Action by</th>
                <th className="px-3 py-2 font-medium min-w-[8rem] max-w-xs">Outcome</th>
                <th className="px-3 py-2 font-medium min-w-[8rem] max-w-xs">Photos / CCTV</th>
                <th className="px-3 py-2 font-medium min-w-[9rem]">Files</th>
                <th className="px-3 py-2 font-medium w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {incidents.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-neutral-500 text-center">
                    No incidents for this entry yet.
                  </td>
                </tr>
              ) : (
                incidents.map((inc) => (
                  <tr key={inc.id} className="align-top hover:bg-neutral-50/80">
                    <td className="px-3 py-2 font-medium text-neutral-900 whitespace-nowrap">{inc.incidentNo}</td>
                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{inc.incidentTime || '—'}</td>
                    <td className="px-3 py-2 text-neutral-700">{inc.location || '—'}</td>
                    <td className="px-3 py-2 text-neutral-700 max-w-xs whitespace-pre-wrap break-words">
                      {inc.details || '—'}
                    </td>
                    <td className="px-3 py-2 text-neutral-700">{inc.actionBy || '—'}</td>
                    <td className="px-3 py-2 text-neutral-700 max-w-xs whitespace-pre-wrap break-words">
                      {inc.outcome || '—'}
                    </td>
                    <td className="px-3 py-2 text-neutral-700 max-w-xs whitespace-pre-wrap break-words">
                      {inc.photosCctvDescription || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1 items-start">
                        {(inc.attachments || []).map((a) => (
                          <span key={a.id} className="inline-flex items-center gap-1 text-xs">
                            <a
                              href={attachmentUrl(a.filePath)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline truncate max-w-[10rem]"
                              title={a.fileName}
                            >
                              {a.fileName}
                            </a>
                            <button
                              type="button"
                              className="text-red-500 shrink-0"
                              onClick={() => handleDeleteAttachment(entry.id, inc.id, a.id)}
                              title="Remove"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <label className="inline-flex items-center gap-1 text-xs text-blue-600 cursor-pointer mt-0.5">
                          <Paperclip className="h-3 w-3 shrink-0" />
                          Add files
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            disabled={saving}
                            onChange={(e) => {
                              handleUpload(entry.id, inc, e.target.files);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="text-red-600 text-xs font-medium hover:underline"
                        onClick={() => handleDeleteIncident(entry.id, inc.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SecurityCommandControlLog: React.FC = () => {
  const { employee } = useAuth();
  const { loading: sessionLoading, securityCommandControlLogEnabled, hasPermission } = usePermissions();
  const [entries, setEntries] = useState<SecurityCommandControlLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionsMenuEntryId, setActionsMenuEntryId] = useState<number | null>(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [actionsExportOpen, setActionsExportOpen] = useState(false);
  const [actionsImportOpen, setActionsImportOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);

  // Downstream modals (implemented in later tasks)
  const [exportingEntryId, setExportingEntryId] = useState<number | null>(null);
  const [exportingKind, setExportingKind] = useState<ExportKind | null>(null);
  const [importEntryId, setImportEntryId] = useState<number | null>(null);
  const [importHistoryEntryId, setImportHistoryEntryId] = useState<number | null>(null);
  const [bulkDeleteEntryId, setBulkDeleteEntryId] = useState<number | null>(null);

  const [newEntry, setNewEntry] = useState({ logDate: '', event: '', location: '' });
  const [showNewEntry, setShowNewEntry] = useState(false);

  const [incidentForms, setIncidentForms] = useState<Record<number, IncidentFormState>>({});
  /** Log entry id when "New incident" modal is open */
  const [addIncidentModalEntryId, setAddIncidentModalEntryId] = useState<number | null>(null);
  const [newIncidentFiles, setNewIncidentFiles] = useState<File[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await securityCommandControlLogApi.getAll();
      setEntries(data || []);
    } catch (e) {
      console.error(e);
      alert('Failed to load log entries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const closeAddIncidentModal = useCallback(() => {
    setAddIncidentModalEntryId(null);
    setNewIncidentFiles([]);
  }, []);

  useEffect(() => {
    if (addIncidentModalEntryId === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) closeAddIncidentModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addIncidentModalEntryId, saving, closeAddIncidentModal]);

  const getIncidentForm = (entryId: number) =>
    incidentForms[entryId] ?? {
      incidentNo: '',
      incidentTime: '',
      location: '',
      details: '',
      actionBy: '',
      outcome: '',
      photosCctvDescription: '',
    };

  const setIncidentFormField = (entryId: number, field: string, value: string) => {
    setIncidentForms((prev) => ({
      ...prev,
      [entryId]: { ...getIncidentForm(entryId), [field]: value },
    }));
  };

  const attachmentUrl = (path: string) => {
    const base = getApiBaseUrlNoPath();
    return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  const toggleEntry = (id: number) => {
    setExpandedId((cur) => (cur === id ? null : id));
  };

  const closeActionsMenu = useCallback(() => {
    setActionsMenuOpen(false);
    setActionsExportOpen(false);
    setActionsImportOpen(false);
    setActionsMenuEntryId(null);
  }, []);

  const openActionsMenu = useCallback((entryId: number) => {
    setActionsMenuEntryId(entryId);
    setActionsMenuOpen(true);
    setActionsExportOpen(false);
    setActionsImportOpen(false);
  }, []);

  // Close actions dropdown when clicking outside
  useEffect(() => {
    if (!actionsMenuOpen) return;
    const handler = (e: MouseEvent) => {
      const el = actionsMenuRef.current;
      if (!el) return;
      if (e.target && el.contains(e.target as Node)) return;
      closeActionsMenu();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionsMenuOpen, closeActionsMenu]);

  const actionsMenuEntry = useMemo(() => {
    if (!actionsMenuEntryId) return null;
    return entries.find((e) => e.id === actionsMenuEntryId) ?? null;
  }, [actionsMenuEntryId, entries]);

  const actionsEnabled = Boolean(actionsMenuEntry && (actionsMenuEntry.incidents || []).length > 0);

  const handleExport = (kind: ExportKind) => {
    if (!actionsMenuEntryId) return;
    setExportingEntryId(actionsMenuEntryId);
    setExportingKind(kind);
    closeActionsMenu();
  };

  const handleOpenImport = () => {
    if (!actionsMenuEntryId) return;
    setImportEntryId(actionsMenuEntryId);
    closeActionsMenu();
  };

  const handleOpenImportHistory = () => {
    if (!actionsMenuEntryId) return;
    setImportHistoryEntryId(actionsMenuEntryId);
    closeActionsMenu();
  };

  const handleOpenBulkDelete = () => {
    if (!actionsMenuEntryId) return;
    setBulkDeleteEntryId(actionsMenuEntryId);
    closeActionsMenu();
  };

  useEffect(() => {
    if (exportingEntryId == null || exportingKind == null) return;
    const entry = entries.find((e) => e.id === exportingEntryId);
    if (!entry) {
      setExportingEntryId(null);
      setExportingKind(null);
      return;
    }
    try {
      exportSecurityCommandControlLogEntry(entry, exportingKind);
    } catch (e) {
      console.error(e);
      alert('Export failed.');
    } finally {
      setExportingEntryId(null);
      setExportingKind(null);
    }
  }, [exportingEntryId, exportingKind, entries]);

  const bulkDeleteEntry = useMemo(() => {
    if (bulkDeleteEntryId == null) return null;
    return entries.find((e) => e.id === bulkDeleteEntryId) ?? null;
  }, [bulkDeleteEntryId, entries]);

  if (sessionLoading) {
    return (
      <div className="flex justify-center items-center py-24 text-neutral-600">Loading…</div>
    );
  }

  if (!securityCommandControlLogEnabled || !hasPermission('security_command_control_log')) {
    return <Navigate to="/" replace />;
  }

  const handleCreateEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEntry.logDate || !newEntry.event.trim() || !newEntry.location.trim()) {
      alert('Date, Event, and Location are required.');
      return;
    }
    try {
      setSaving(true);
      await securityCommandControlLogApi.createEntry({
        logDate: newEntry.logDate,
        event: newEntry.event.trim(),
        location: newEntry.location.trim(),
      });
      setNewEntry({ logDate: '', event: '', location: '' });
      setShowNewEntry(false);
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Failed to create entry.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (id: number) => {
    if (!(await appConfirm('Delete this log entry and all incidents?', { danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await securityCommandControlLogApi.deleteEntry(id);
      if (expandedId === id) setExpandedId(null);
      if (addIncidentModalEntryId === id) closeAddIncidentModal();
      await load();
    } catch {
      alert('Failed to delete entry.');
    }
  };

  const handleAddIncident = async (entryId: number, filesToUpload: File[] = []) => {
    const f = getIncidentForm(entryId);
    if (!f.incidentNo.trim()) {
      alert('Incident No is required.');
      return;
    }
    try {
      setSaving(true);
      const created = await securityCommandControlLogApi.createIncident(entryId, {
        incidentNo: f.incidentNo.trim(),
        incidentTime: f.incidentTime,
        location: f.location,
        details: f.details,
        actionBy: f.actionBy,
        outcome: f.outcome,
        photosCctvDescription: f.photosCctvDescription || undefined,
      });
      if (filesToUpload.length > 0 && created?.id) {
        await securityCommandControlLogApi.uploadAttachments(entryId, created.id, filesToUpload);
      }
      setIncidentForms((prev) => ({
        ...prev,
        [entryId]: {
          incidentNo: '',
          incidentTime: '',
          location: '',
          details: '',
          actionBy: '',
          outcome: '',
          photosCctvDescription: '',
        },
      }));
      await load();
      closeAddIncidentModal();
    } catch {
      alert('Failed to add incident.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIncident = async (entryId: number, incidentId: number) => {
    if (!(await appConfirm('Delete this incident?', { danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await securityCommandControlLogApi.deleteIncident(entryId, incidentId);
      await load();
    } catch {
      alert('Failed to delete incident.');
    }
  };

  const handleUpload = async (entryId: number, incident: SecurityCommandControlLogIncident, files: FileList | null) => {
    if (!files?.length) return;
    const arr = Array.from(files);
    try {
      setSaving(true);
      await securityCommandControlLogApi.uploadAttachments(entryId, incident.id, arr);
      await load();
    } catch {
      alert('Failed to upload file(s).');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (entryId: number, incidentId: number, attachmentId: number) => {
    if (!(await appConfirm('Remove this attachment?', { danger: true, confirmLabel: 'Remove' }))) return;
    try {
      await securityCommandControlLogApi.deleteAttachment(entryId, incidentId, attachmentId);
      await load();
    } catch {
      alert('Failed to delete attachment.');
    }
  };

  const expandedBodyProps: Omit<EntryExpandedBodyProps, 'entry'> = {
    employee,
    saving,
    getIncidentForm,
    setIncidentFormField,
    attachmentUrl,
    handleDeleteIncident,
    handleDeleteAttachment,
    handleUpload,
    onOpenAddIncident: (entryId) => {
      setNewIncidentFiles([]);
      setAddIncidentModalEntryId(entryId);
    },
  };

  const addIncidentModalEntry =
    addIncidentModalEntryId !== null ? entries.find((e) => e.id === addIncidentModalEntryId) : undefined;
  const modalEntryId = addIncidentModalEntryId;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 md:max-w-[min(100%,120rem)]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Security Command and Control Log</h1>
          <p className="text-sm text-neutral-600 mt-1">
            Live programme incident logging. Logger is the signed-in user for each parent row.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewEntry((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          New log entry
        </button>
      </div>

      {showNewEntry && (
        <form onSubmit={handleCreateEntry} className="mb-6 p-4 border border-neutral-200 rounded-lg bg-white shadow-sm space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Date</label>
              <input
                type="date"
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                value={newEntry.logDate}
                onChange={(e) => setNewEntry((p) => ({ ...p, logDate: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-neutral-600 mb-1">Event</label>
              <input
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                value={newEntry.event}
                onChange={(e) => setNewEntry((p) => ({ ...p, event: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-neutral-600 mb-1">Location</label>
              <input
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                value={newEntry.location}
                onChange={(e) => setNewEntry((p) => ({ ...p, location: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Save entry
            </button>
            <button type="button" className="px-4 py-2 text-sm text-neutral-600" onClick={() => setShowNewEntry(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-neutral-600">Loading entries…</div>
      ) : entries.length === 0 ? (
        <p className="text-neutral-600">No log entries yet.</p>
      ) : (
        <>
          {/* Mobile: expandable cards */}
          <div className="md:hidden space-y-2">
            {entries.map((entry) => {
              const open = expandedId === entry.id;
              const hasIncidents = (entry.incidents || []).length > 0;
              return (
                <div key={entry.id} className="border border-neutral-200 rounded-lg bg-white shadow-sm overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50"
                    onClick={() => toggleEntry(entry.id)}
                  >
                    {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-neutral-900 truncate">{entry.event}</div>
                      <div className="text-xs text-neutral-500">
                        {entry.logDate} · {entry.location} · Logger: {entry.loggerName || `#${entry.loggerEmployeeId}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="p-2 text-neutral-700 hover:bg-neutral-100 rounded"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (actionsMenuOpen && actionsMenuEntryId === entry.id) closeActionsMenu();
                        else openActionsMenu(entry.id);
                      }}
                      title="Actions"
                      aria-haspopup="menu"
                      aria-expanded={actionsMenuOpen && actionsMenuEntryId === entry.id}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        handleDeleteEntry(entry.id);
                      }}
                      title="Delete entry"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </button>
                  {open && (
                    <div className="border-t border-neutral-100 px-4 py-4 bg-neutral-50/80">
                      <EntryExpandedBody entry={entry} {...expandedBodyProps} />
                    </div>
                  )}
                  {actionsMenuOpen && actionsMenuEntryId === entry.id && (
                    <div className="px-4 pb-3">
                      <div
                        className="mt-2 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow"
                        role="menu"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                            hasIncidents ? 'text-neutral-800 hover:bg-neutral-50' : 'text-neutral-400 cursor-not-allowed'
                          }`}
                          disabled={!hasIncidents}
                          onClick={() => setActionsExportOpen((v) => !v)}
                        >
                          <span className="inline-flex items-center gap-2">
                            <Download className="h-4 w-4" />
                            Export
                          </span>
                          <ChevronRight className={`h-4 w-4 transition-transform ${actionsExportOpen ? 'rotate-90' : ''}`} />
                        </button>
                        {actionsExportOpen && (
                          <div className="border-t border-neutral-200">
                            <button
                              type="button"
                              role="menuitem"
                              className="flex w-full items-center gap-2 px-6 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                              onClick={() => handleExport('pdf')}
                            >
                              Save as PDF
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="flex w-full items-center gap-2 px-6 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                              onClick={() => handleExport('excel')}
                            >
                              Excel
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="flex w-full items-center gap-2 px-6 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                              onClick={() => handleExport('csv')}
                            >
                              CSV
                            </button>
                          </div>
                        )}

                        <button
                          type="button"
                          role="menuitem"
                          className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm border-t border-neutral-200 ${
                            hasIncidents ? 'text-neutral-800 hover:bg-neutral-50' : 'text-neutral-400 cursor-not-allowed'
                          }`}
                          disabled={!hasIncidents}
                          onClick={() => setActionsImportOpen((v) => !v)}
                        >
                          <span className="inline-flex items-center gap-2">
                            <Upload className="h-4 w-4" />
                            Import
                          </span>
                          <ChevronRight className={`h-4 w-4 transition-transform ${actionsImportOpen ? 'rotate-90' : ''}`} />
                        </button>
                        {actionsImportOpen && (
                          <div className="border-t border-neutral-200">
                            <button
                              type="button"
                              role="menuitem"
                              className="flex w-full items-center gap-2 px-6 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                              onClick={handleOpenImport}
                            >
                              <FileSpreadsheet className="h-4 w-4 text-neutral-500" />
                              Import From File
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="flex w-full items-center gap-2 px-6 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                              onClick={handleOpenImportHistory}
                            >
                              <History className="h-4 w-4 text-neutral-500" />
                              Import History
                            </button>
                          </div>
                        )}

                        <button
                          type="button"
                          role="menuitem"
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm border-t border-neutral-200 ${
                            hasIncidents ? 'text-neutral-800 hover:bg-neutral-50' : 'text-neutral-400 cursor-not-allowed'
                          }`}
                          disabled={!hasIncidents}
                          onClick={handleOpenBulkDelete}
                        >
                          <Trash2 className="h-4 w-4 text-neutral-500" />
                          Bulk delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop: log entries as table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-sm text-sm md:text-[15px] md:leading-relaxed">
            <table className="min-w-full text-left">
              <thead className="bg-neutral-50 text-neutral-600 border-b border-neutral-200">
                <tr>
                  <th className="w-10 px-2 py-3" aria-label="Expand" />
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Date</th>
                  <th className="px-3 py-3 font-medium min-w-[12rem]">Event</th>
                  <th className="px-3 py-3 font-medium min-w-[10rem]">Location</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Logger</th>
                  <th className="px-3 py-3 font-medium w-28 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {entries.map((entry) => {
                  const open = expandedId === entry.id;
                  return (
                    <React.Fragment key={entry.id}>
                      <tr
                        className={`align-middle hover:bg-neutral-50/80 ${open ? 'bg-neutral-50/50' : ''}`}
                      >
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            className="p-1.5 rounded text-neutral-600 hover:bg-neutral-200/60"
                            onClick={() => toggleEntry(entry.id)}
                            aria-expanded={open}
                            title={open ? 'Collapse' : 'Expand'}
                          >
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{entry.logDate}</td>
                        <td className="px-3 py-2 font-medium text-neutral-900">{entry.event}</td>
                        <td className="px-3 py-2 text-neutral-700">{entry.location}</td>
                        <td className="px-3 py-2 text-neutral-600">
                          {entry.loggerName || `#${entry.loggerEmployeeId}`}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center justify-end gap-1">
                            <button
                              type="button"
                              className="inline-flex items-center justify-center p-2 text-neutral-700 hover:bg-neutral-100 rounded"
                              onClick={() => {
                                if (actionsMenuOpen && actionsMenuEntryId === entry.id) closeActionsMenu();
                                else openActionsMenu(entry.id);
                              }}
                              title="Actions"
                              aria-haspopup="menu"
                              aria-expanded={actionsMenuOpen && actionsMenuEntryId === entry.id}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded"
                              onClick={() => handleDeleteEntry(entry.id)}
                              title="Delete entry"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-neutral-50/80">
                          <td colSpan={6} className="px-4 py-4 border-t border-neutral-100">
                            <EntryExpandedBody entry={entry} {...expandedBodyProps} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Desktop floating actions menu */}
      {actionsMenuOpen && actionsMenuEntry && (
        <div className="fixed inset-0 z-[60]" aria-hidden={!actionsMenuOpen}>
          <div className="absolute inset-0" />
          <div
            ref={actionsMenuRef}
            className="absolute right-6 top-[7.5rem] w-64 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
            role="menu"
          >
            <div className="px-3 py-2 border-b border-neutral-200">
              <div className="text-xs text-neutral-500">Entry</div>
              <div className="text-sm font-semibold text-neutral-900 truncate" title={actionsMenuEntry.event}>
                {actionsMenuEntry.event}
              </div>
            </div>

            <button
              type="button"
              role="menuitem"
              className={`flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm ${
                actionsEnabled ? 'text-neutral-800 hover:bg-neutral-50' : 'text-neutral-400 cursor-not-allowed'
              }`}
              disabled={!actionsEnabled}
              onClick={() => {
                setActionsExportOpen((v) => !v);
                setActionsImportOpen(false);
              }}
            >
              <span className="inline-flex items-center gap-2">
                <Download className="h-4 w-4 text-neutral-500" />
                Export
              </span>
              <ChevronRight className={`h-4 w-4 transition-transform ${actionsExportOpen ? 'rotate-90' : ''}`} />
            </button>
            {actionsExportOpen && (
              <div className="border-t border-neutral-200">
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-7 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                  onClick={() => handleExport('pdf')}
                >
                  Save as PDF
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-7 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                  onClick={() => handleExport('excel')}
                >
                  Excel
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-7 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                  onClick={() => handleExport('csv')}
                >
                  CSV
                </button>
              </div>
            )}

            <button
              type="button"
              role="menuitem"
              className={`flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm border-t border-neutral-200 ${
                actionsEnabled ? 'text-neutral-800 hover:bg-neutral-50' : 'text-neutral-400 cursor-not-allowed'
              }`}
              disabled={!actionsEnabled}
              onClick={() => {
                setActionsImportOpen((v) => !v);
                setActionsExportOpen(false);
              }}
            >
              <span className="inline-flex items-center gap-2">
                <Upload className="h-4 w-4 text-neutral-500" />
                Import
              </span>
              <ChevronRight className={`h-4 w-4 transition-transform ${actionsImportOpen ? 'rotate-90' : ''}`} />
            </button>
            {actionsImportOpen && (
              <div className="border-t border-neutral-200">
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-7 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                  onClick={handleOpenImport}
                >
                  <FileSpreadsheet className="h-4 w-4 text-neutral-500" />
                  Import From File
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-7 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                  onClick={handleOpenImportHistory}
                >
                  <History className="h-4 w-4 text-neutral-500" />
                  Import History
                </button>
              </div>
            )}

            <button
              type="button"
              role="menuitem"
              className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm border-t border-neutral-200 ${
                actionsEnabled ? 'text-neutral-800 hover:bg-neutral-50' : 'text-neutral-400 cursor-not-allowed'
              }`}
              disabled={!actionsEnabled}
              onClick={handleOpenBulkDelete}
            >
              <Trash2 className="h-4 w-4 text-neutral-500" />
              Bulk delete
            </button>
          </div>
        </div>
      )}

      {/* Placeholder states until next todos implement the modals/handlers */}
      {exportingEntryId != null && exportingKind != null ? null : null}
      <ImportSecurityCommandControlLogIncidentsModal
        open={importEntryId != null}
        onClose={() => setImportEntryId(null)}
        entryId={importEntryId ?? 0}
        entryLabel={(entries.find((e) => e.id === importEntryId)?.event) || undefined}
        onImportCommitted={() => void load()}
      />
      <SecurityCommandControlLogImportHistoryModal
        open={importHistoryEntryId != null}
        onClose={() => setImportHistoryEntryId(null)}
        entryId={importHistoryEntryId ?? 0}
        entryLabel={(entries.find((e) => e.id === importHistoryEntryId)?.event) || undefined}
      />
      <SecurityCommandControlLogBulkDeleteModal
        open={bulkDeleteEntryId != null && bulkDeleteEntry != null}
        onClose={() => setBulkDeleteEntryId(null)}
        entry={bulkDeleteEntry}
        onDeleted={() => void load()}
      />

      {modalEntryId !== null && addIncidentModalEntry && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-incident-modal-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close dialog"
            disabled={saving}
            onClick={() => !saving && closeAddIncidentModal()}
          />
          <div className="relative z-10 flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col rounded-t-2xl border border-neutral-200 bg-white shadow-xl sm:rounded-2xl sm:max-h-[85vh]">
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-neutral-200 px-4 py-3 sm:px-5">
              <div className="min-w-0 pr-2">
                <h2 id="add-incident-modal-title" className="text-lg font-semibold text-neutral-900">
                  New incident
                </h2>
                <p className="mt-0.5 truncate text-xs text-neutral-500" title={`${addIncidentModalEntry.logDate} · ${addIncidentModalEntry.event}`}>
                  {addIncidentModalEntry.logDate} · {addIncidentModalEntry.event}
                </p>
              </div>
              <button
                type="button"
                disabled={saving}
                className="rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-50"
                onClick={() => closeAddIncidentModal()}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form
              className="flex min-h-0 flex-1 flex-col overflow-hidden text-sm md:text-[15px]"
              onSubmit={async (e) => {
                e.preventDefault();
                await handleAddIncident(modalEntryId, newIncidentFiles);
              }}
            >
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-neutral-600 md:text-sm">Incident no. *</label>
                    <input
                      required
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={getIncidentForm(modalEntryId).incidentNo}
                      onChange={(e) => setIncidentFormField(modalEntryId, 'incidentNo', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-neutral-600 md:text-sm">Time</label>
                    <div className="relative">
                      <input
                        type="time"
                        value={toHtmlTimeValue(getIncidentForm(modalEntryId).incidentTime)}
                        onChange={(e) => setIncidentFormField(modalEntryId, 'incidentTime', e.target.value)}
                        className="w-full rounded-md border border-neutral-300 px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="--:--"
                      />
                      <Clock className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-neutral-600 md:text-sm">Location</label>
                    <input
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={getIncidentForm(modalEntryId).location}
                      onChange={(e) => setIncidentFormField(modalEntryId, 'location', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-neutral-600 md:text-sm">Details</label>
                    <textarea
                      rows={3}
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={getIncidentForm(modalEntryId).details}
                      onChange={(e) => setIncidentFormField(modalEntryId, 'details', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-neutral-600 md:text-sm">Action by</label>
                    <input
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={getIncidentForm(modalEntryId).actionBy}
                      onChange={(e) => setIncidentFormField(modalEntryId, 'actionBy', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-neutral-600 md:text-sm">Outcome</label>
                    <textarea
                      rows={2}
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={getIncidentForm(modalEntryId).outcome}
                      onChange={(e) => setIncidentFormField(modalEntryId, 'outcome', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-neutral-600 md:text-sm">Photos / CCTV description</label>
                    <textarea
                      rows={2}
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={getIncidentForm(modalEntryId).photosCctvDescription}
                      onChange={(e) => setIncidentFormField(modalEntryId, 'photosCctvDescription', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2 border-t border-neutral-100 pt-3">
                    <label className="mb-1 block text-xs font-medium text-neutral-600 md:text-sm">Attachments</label>
                    {newIncidentFiles.length > 0 && (
                      <ul className="mb-2 space-y-1">
                        {newIncidentFiles.map((file, idx) => (
                          <li
                            key={`${file.name}-${idx}-${file.size}`}
                            className="flex items-center justify-between gap-2 rounded border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-neutral-800"
                          >
                            <span className="min-w-0 truncate" title={file.name}>
                              {file.name}
                            </span>
                            <button
                              type="button"
                              disabled={saving}
                              className="shrink-0 text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                              onClick={() => setNewIncidentFiles((prev) => prev.filter((_, i) => i !== idx))}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700">
                      <Paperclip className="h-4 w-4 shrink-0" />
                      Add files
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        disabled={saving}
                        onChange={(e) => {
                          const list = e.target.files;
                          if (list?.length) {
                            setNewIncidentFiles((prev) => [...prev, ...Array.from(list)]);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <p className="mt-1 text-xs text-neutral-500">Files are uploaded after the incident is saved.</p>
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-neutral-200 bg-neutral-50/80 px-4 py-3 sm:flex-row sm:justify-end sm:px-5">
                <button
                  type="button"
                  disabled={saving}
                  className="w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 sm:w-auto"
                  onClick={() => closeAddIncidentModal()}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 sm:w-auto"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving…' : 'Save incident'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityCommandControlLog;
